import { Injectable } from '@angular/core';

import moment from 'moment';
import jsPDF from 'jspdf';

import { PaymentTransactionGrid } from 'src/app/models/grids';
import { MemberAccount } from 'src/app/models/member';
import { AlertService } from './alert.service';
import { Location as LocationV2 } from 'src/app/models/location';

export interface ExportData {
  transactions: PaymentTransactionGrid[];
  memberAccount: MemberAccount | null;
  facilityName?: string;
  location?: LocationV2 | null;
}

export interface ExportCheckinsData {
  checkinDates: string[]; // YYYY-MM-DD
  memberAccount: MemberAccount | null;
  facilityName?: string;
  location?: LocationV2 | null;
}

@Injectable({
  providedIn: 'root'
})
export class ExportService {
  constructor(private alertService: AlertService) {}

  generateCSV(transactions: PaymentTransactionGrid[], memberName: string): void {
    if (!transactions || transactions.length === 0) {
      this.alertService.info('No transactions found for the selected period.');
      return;
    }

    const headers = ['MemberName', 'ReceiptEmail', 'Amount', 'RefundedAmount', 'Currency', 'Description', 'Comment', 'CreatedAt'];

    const csvRows = transactions.map((t) => [
      t.MemberName,
      t.ReceiptEmail,
      t.Amount ? (t.Amount / 100).toFixed(2) : '0.00',
      t.RefundedAmount ? (t.RefundedAmount / 100).toFixed(2) : '0.00',
      t.Currency,
      t.Description,
      t.Comment || '',
      moment((t as any).CreatedAt).format('YYYY-MM-DD HH:mm:ss')
    ]);

    const csvContent = [headers, ...csvRows]
      .map((row) => row.map((field) => `"${String(field ?? '').replaceAll('"', '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `statement_${memberName || 'member'}_${moment().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  generatePDF(data: ExportData): void {
    const { transactions, memberAccount, facilityName, location } = data;

    if (!transactions || transactions.length === 0) {
      this.alertService.info('No transactions found for the selected period.');
      return;
    }

    const doc = new jsPDF();

    // Header (match requested layout)
    let y = 20;

    doc.setFontSize(14);
    doc.text(facilityName || 'Drop Fitness', 20, y);
    y += 7;

    doc.setFontSize(9);

    if (location) {
      const parts: string[] = [];
      if (location.address_line1) parts.push(location.address_line1);
      if (location.city || location.state || location.zip_code) {
        const cityStateZip = [location.city, location.state, location.zip_code].filter(Boolean).join(', ');
        if (cityStateZip) parts.push(cityStateZip);
      }

      if (parts.length) {
        doc.text(parts.join(', '), 20, y);
        y += 5;
      }

      if (location.phone) {
        doc.text(`Phone: ${location.phone}`, 20, y);
        y += 5;
      }
    }

    doc.text(`${moment().format('MM/DD/YYYY')}`, 20, y);
    y += 10;

    // Member Information block
    doc.setFontSize(10);
    doc.text('Member Information:', 20, y);
    y += 8;

    doc.setFontSize(9);
    doc.text(`Name: ${memberAccount?.Person?.FullName || 'N/A'}`, 20, y);
    y += 6;

    doc.text(`Account ID: ${(memberAccount as any)?.Id || 'N/A'}`, 20, y);
    y += 6;

    doc.text(`Phone: ${formatPhone(memberAccount)}`, 20, y);
    y += 6;

    doc.text(`Email: ${memberAccount?.Person?.Email || 'N/A'}`, 20, y);
    y += 12;

    // Table header
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Date', 20, y);
    doc.text('Amount', 60, y);
    doc.text('Refunded', 80, y);
    doc.text('Currency', 105, y);
    doc.text('Description', 130, y);
    doc.line(20, y + 3, 200, y + 3);

    y += 10;

    doc.setFont('helvetica', 'normal');
    transactions.forEach((t, idx) => {
      if (y > 280) {
        doc.addPage();
        y = 25;
      }

      if (idx % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(20, y - 4, 180, 6, 'F');
      }

      const amount = t.Amount ? `$${(t.Amount / 100).toFixed(2)}` : '$0.00';
      const refunded = t.RefundedAmount ? `$${(t.RefundedAmount / 100).toFixed(2)}` : '$0.00';

      doc.text(moment((t as any).CreatedAt).format('MM/DD/YYYY hh:mm A'), 20, y);
      doc.text(amount, 60, y);
      doc.text(refunded, 80, y);
      doc.text((t.Currency || '').toUpperCase(), 105, y);

      const wrappedDesc = doc.splitTextToSize(t.Description || '', 70);
      doc.text(wrappedDesc, 130, y);

      y += 6 + (wrappedDesc.length - 1) * 4;
    });

    const filename = `statement_${memberAccount?.Person?.FullName || 'member'}_${moment().format('YYYY-MM-DD')}.pdf`;
    doc.save(filename);
  }

  generateCheckinsCSV(checkinDates: string[], memberAccount: MemberAccount | null): void {
    if (!checkinDates || checkinDates.length === 0) {
      this.alertService.info('No check-ins found for the selected period.');
      return;
    }

    const name = memberAccount?.Person?.FullName || 'member';
    const dob = memberAccount?.Person?.BirthDate ? moment(memberAccount.Person.BirthDate).format('MM/DD/YYYY') : '';

    const headers = ['Name', 'Date of birth', 'Checkin date'];
    const rows = checkinDates.map((d) => [name, dob, d]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((field) => `"${String(field ?? '').replaceAll('"', '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `checkins_${name}_${moment().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  generateCheckinsPDF(data: ExportCheckinsData): void {
    const { checkinDates, memberAccount, facilityName, location } = data;

    if (!checkinDates || checkinDates.length === 0) {
      this.alertService.info('No check-ins found for the selected period.');
      return;
    }

    const doc = new jsPDF();

    // Header
    let y = 20;

    doc.setFontSize(14);
    doc.text(facilityName || 'Drop Fitness', 20, y);
    y += 7;

    doc.setFontSize(9);

    if (location) {
      const parts: string[] = [];
      if (location.address_line1) parts.push(location.address_line1);
      if (location.city || location.state || location.zip_code) {
        const cityStateZip = [location.city, location.state, location.zip_code].filter(Boolean).join(', ');
        if (cityStateZip) parts.push(cityStateZip);
      }

      if (parts.length) {
        doc.text(parts.join(', '), 20, y);
        y += 5;
      }

      if (location.phone) {
        doc.text(`Phone: ${location.phone}`, 20, y);
        y += 5;
      }
    }

    doc.text(`${moment().format('MM/DD/YYYY')}`, 20, y);
    y += 10;

    // Member Info section
    doc.setFontSize(10);
    doc.text('Member Information:', 20, y);
    y += 8;

    doc.setFontSize(9);
    doc.text(`Name: ${memberAccount?.Person?.FullName || 'N/A'}`, 20, y);
    y += 6;

    const dob = memberAccount?.Person?.BirthDate ? moment(memberAccount.Person.BirthDate).format('MM/DD/YYYY') : 'N/A';
    doc.text(`Date of birth: ${dob}`, 20, y);
    y += 6;

    doc.text(`Phone: ${formatPhone(memberAccount)}`, 20, y);
    y += 6;

    doc.text(`Email: ${memberAccount?.Person?.Email || 'N/A'}`, 20, y);
    y += 12;

    // Check-in dates section
    doc.setFontSize(10);
    doc.text('Check-in dates:', 20, y);
    y += 8;

    doc.setFontSize(9);

    // Render dates in a 4-column table-like layout to use space efficiently
    const leftX = 20;
    const colCount = 4;
    const colWidth = 45; // tuned for A4 portrait with margins
    const rowHeight = 6;

    for (let i = 0; i < checkinDates.length; i++) {
      const col = i % colCount;
      const row = Math.floor(i / colCount);

      const x = leftX + col * colWidth;
      const yRow = y + row * rowHeight;

      if (yRow > 280) {
        doc.addPage();
        // reset cursor near top for continuation
        y = 25;
        i -= 1; // re-render same item on new page
        continue;
      }

      doc.text(checkinDates[i], x, yRow);
    }

    // advance y after the grid
    y += Math.ceil(checkinDates.length / colCount) * rowHeight;

    y += 6;
    if (y > 280) {
      doc.addPage();
      y = 25;
    }

    doc.setFontSize(10);
    doc.text(`Total: ${checkinDates.length}`, 20, y);

    const filename = `checkins_${memberAccount?.Person?.FullName || 'member'}_${moment().format('YYYY-MM-DD')}.pdf`;
    doc.save(filename);
  }
}

function formatPhone(memberAccount: MemberAccount | null): string {
  const dial = memberAccount?.Person?.DialCode || '';
  const phone = memberAccount?.Person?.PhoneNumber || '';
  const raw = `${dial}${phone}`.trim();
  return raw || 'N/A';
}
